import React, {useEffect, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import InputForm from "../../../../components/input/input-form.jsx";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx"
import SelectComponent from "../../../../components/select/select-component.jsx";
import teamService from "../../../../services/web/team-service.js";
import userService from "../../../../services/web/user-service.js";
import DocumentsCellRenderer from "./components/documents-cell-renderer.jsx";
import {handleFilters} from "../components/handle-filters.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import SentryRow from "../../../../components/template/sentry-row.jsx";

const statusOptions = [
    {value: "active", label: "Ativo"},
    {value: "inactive", label: "Inativo"},
]

const cnhCategoryOptions = [
    {value: "A", label: "A"},
    {value: "B", label: "B"},
    {value: "C", label: "C"},
    {value: "D", label: "D"},
    {value: "E", label: "E"},
]

const documentsOptions = [
    {value: "expired", label: "Vencido"},
    {value: "toExpire", label: "A vencer"},
]

const initialFilter = {
    cpf: "",
    name: "",
    nameWar: "",
    status: "active",
    cnhCategory: "",
    documents: "",
    training: "",
    team: ""
}

const PersonalList = () => {
    const navigate = navigateHook();
    const [users, setUsers] = useState([]);
    const [usersFiltered, setUsersFiltered] = useState([]);
    const [filter, setFilter] = useState(initialFilter)

    const gridRef = useRef();
    const [columnDefs, setColumnDefs] = useState([]);

    useEffect(() => {
        const columns = [
            {headerName: "CPF", field: "cpf", width: 120, valueFormatter: ({value}) => projectUtils.formatCpfToView(value),
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Nome de guerra", field: "nameWar", sort: "asc",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Nome", field: "name", flex: 1,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Equipe", field: "team",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Categoria CNH", field: "cnhCategory",
                valueFormatter: ({value}) => {
                    if (value) {
                        return  JSON.parse(value).join(", ");
                    }

                    return value;
                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Documentos", field: "documents", cellRenderer: DocumentsCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Formação", field: "training", cellRenderer: DocumentsCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
        ];

        setColumnDefs(columns);
    }, []);

    const {data: teams} = useQuery({
        queryFn: async () => {
            const {data} = await teamService.getAll();
            return data ?? [];
        },
        queryKey: ['teams-options'],
    });

    const {data, status} = useQuery({
        queryFn: async () => {
            const {data} = await userService.personalList();
            return data ?? [];
        },
        queryKey: ['agents-personal-list'],
    });

    useEffect(() => {
        if (status === 'success') {
            gridRef.current?.api?.showLoadingOverlay();

            const dataFormatted = data.map(item => {
                const {documents, training} = item;

                const statusDocument = getDocumentsStatus(documents);
                const statusTraining = getTrainingStatus(training);

                return {...item, documents: statusDocument, training: statusTraining}
            });

            setUsers(dataFormatted)
            setUsersFiltered(handleFilters(dataFormatted, filter));
        }
    }, [data, status]);

    const getTrainingStatus = (value) => {
        if (value) {
            const dataFormatted = JSON.parse(value);
            // workaround for 'null' values
            if (!dataFormatted) return null

            for (const item of dataFormatted) {
                const {dateExpiration, itemId} = item;

                const hasRelated = dataFormatted.find(item => item.relatedItemId === itemId)
                if (hasRelated) continue;

                if (getStatus(dateExpiration, false)) {
                    return getStatus(dateExpiration, false)
                }
            }
        }

        return null;
    }

    const getDocumentsStatus = (value) => {
        const dataFormatted = JSON.parse(value);
        if (dataFormatted) {
            const fields = [
                'expirationCnh',
                'carryDueDate',
                'shotDueDate',
                'psychoDueDate',
                'crafDueDate',
                'toxicologicalDueDate',
                'longGunLicenceDueDate',
            ];

            for (const field of fields) {
                const status = getStatus(dataFormatted[field], true);
                if (status) {
                    return status;
                }
            }
        }

        return null;
    }

    const getStatus = (date, withHour) => {
        if (!date) return null;

        const dateObj = withHour ? new Date(date + ' 00:00:00') : new Date(date);
        const today = new Date();

        if (dateObj.getTime() <= today.getTime()) return "expired";

        const todayPlus60Days = new Date();
        todayPlus60Days.setDate(today.getDate() + 60);

        if (dateObj.getTime() > today.getTime() && dateObj.getTime() <= todayPlus60Days.getTime()) return "toExpire";

        return null;
    }

    const onChange = ({target}) => {
        let {id, value} = target;

        setFilter(prev => ({...prev, [id]: value}));
    }

    const handleKeyDown = ({key}) => {
        if (key === 'Enter') apply(filter);
        if (key === 'Escape') clearFilter()
    }

    const clearFilter = () => {
        setFilter(initialFilter);
        apply(initialFilter);
    }

    const apply = (currentFilter) => {
        gridRef.current?.api?.showLoadingOverlay();
        setUsersFiltered(handleFilters(users, currentFilter));
    }

    const onCellClicked = ({data, event}) => {
        let isBlank = false;
        let urlDev = `/personal-agents/form/${data.id}`;
        if (event.ctrlKey) {
            isBlank = true
        }

        navigate(urlDev, isBlank);
    }

    const newAgent = () => {
        const urlDev = "/personal-agents/form"
        navigate(urlDev);
    }

    return (
        <>
            <Template>
                <Template.Header>
                    <Template.Title>
                        Agentes
                    </Template.Title>

                    <Button onClick={newAgent}>
                        <FaPlus/> Adicionar agente
                    </Button>
                </Template.Header>

                <Template.Body>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className="col-span-2">
                                    <InputForm
                                        id="cpf"
                                        label="CPF"
                                        value={filter.cpf}
                                        onChange={onChange}
                                        onBlur={onChange}
                                        onKeyDown={handleKeyDown}
                                        mask={'000.000.000-00'}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <InputForm id="name" value={filter.name} onChange={onChange} onKeyDown={handleKeyDown}
                                               label="Nome"/>
                                </div>
                                <div className="col-span-3">
                                    <InputForm id="nameWar" value={filter.nameWar} onChange={onChange} onKeyDown={handleKeyDown}
                                               label="Nome de guerra"/>
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        id="status"
                                        value={statusOptions.find(s => s.value === filter.status)}
                                        options={statusOptions}
                                        label={"Status"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, status: selected?.value ?? ""}))
                                        }
                                    />
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        id="team"
                                        value={teams?.find(option => option.label === filter.team) ?? ""}
                                        options={teams ?? []}
                                        label={"Equipe"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, team: selected?.label ?? ""}))
                                        }
                                    />
                                </div>

                                <div className="col-span-4">
                                    <SelectComponent
                                        id="cnhCategory"
                                        value={cnhCategoryOptions?.find(s => s.value === filter.cnhCategory) ?? ""}
                                        options={cnhCategoryOptions}
                                        label={"Categoria CNH"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, cnhCategory: selected?.value ?? ""}))
                                        }
                                    />
                                </div>

                                <div className="col-span-4">
                                    <SelectComponent
                                        id="documents"
                                        value={documentsOptions?.find(s => s.value === filter.documents) ?? ""}
                                        options={documentsOptions}
                                        label={"Documentos"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, documents: selected?.value ?? ""}))
                                        }
                                    />
                                </div>
                                <div className="col-span-4 flex gap-4">
                                    <SelectComponent
                                        classNameWrapper={"w-full"}
                                        id="training"
                                        value={documentsOptions?.find(s => s.value === filter.training) ?? ""}
                                        options={documentsOptions}
                                        label={"Formação"}
                                        onChange={(selected) =>
                                            setFilter(prev => ({...prev, training: selected?.value ?? ""}))
                                        }
                                    />
                                    <div className="flex justify-start gap-2">
                                        <Button target={"primary-outline"} title="Limpar" onClick={() => clearFilter()}>
                                            <FaEraser />
                                        </Button>
                                        <Button title="Buscar" onClick={() => apply(filter)}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card className={"mt-2"}>
                        <Card.Body>
                            <div className="aggrid-row-pointer h-[calc(100vh-201px)]">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={usersFiltered}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme()}
                                />
                            </div>
                        </Card.Body>
                    </Card>

                </Template.Body>
            </Template>
        </>
    );
};

export default PersonalList;